import { DialogLayout, InlineError, Stack } from '@/ui';
import { Button, type DialogProps } from '@/ui/primitives';
import type { KeyboardEvent, ReactNode } from 'react';

type AddDialogProps = {
  open: boolean;
  title: string;
  saving?: boolean;
  disableSave?: boolean;
  saveLabel?: string;
  description?: ReactNode;
  errorMessage?: string | null;
  contentSx?: DialogProps['sx'];
  onClose: () => void;
  onSave: () => void;
  children: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
};

export const AddDialog = ({
  open,
  title,
  saving = false,
  disableSave = false,
  saveLabel = 'Save',
  description,
  errorMessage,
  contentSx,
  onClose,
  onSave,
  children,
  maxWidth = 'sm',
}: AddDialogProps): ReactNode => {
  const handleContentKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (saving || disableSave) return;
    if (event.defaultPrevented || event.key !== 'Enter') return;
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName;
    if (tagName === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    onSave();
  };

  return (
    <DialogLayout
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      title={title}
      description={description}
      contentSx={contentSx}
      onContentKeyDownCapture={handleContentKeyDownCapture}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saving}
            aria-label="Cancel dialog"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={saving || disableSave}
            aria-label="Save dialog"
          >
            {saving ? 'Saving...' : saveLabel}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        {children}
        {errorMessage ? <InlineError message={errorMessage} /> : null}
      </Stack>
    </DialogLayout>
  );
};
